import { useRef, type KeyboardEvent, type ReactNode } from "react";
import { NavLink } from "react-router";

/**
 * The tabs of the console — the only ones. Two kinds, because two different things are called
 * "tabs" and they do not mean the same to a screen reader:
 *
 *   - ROUTE tabs (`tabs` = `{ to, label }`): each tab is a page of its own, with its own address —
 *     Emails / Templates / Campaigns. A `<nav>` of links, the current one `aria-current="page"`.
 *     `PageHeader` draws them for a `section`; you rarely write this one yourself.
 *   - STATE tabs (`value` + `onChange`): the views of ONE page — Write / Preview of the Markdown
 *     editor, the recognized / unknown tracks of the metadata cache. The full ARIA tab pattern:
 *     a `tablist`, `role="tab"` + `aria-selected` + `aria-controls`, one tab in the tab order and the
 *     arrow keys (Home / End too) to move between them, and a `TabPanel` around what each one shows.
 *     `activation` says what the arrows do: "automatic" (the default) selects the tab they land on —
 *     right for views that are cheap and already in memory (Write / Preview); "manual" only MOVES THE
 *     FOCUS, and Enter or Space selects — right when selecting costs a request (the metadata cache
 *     asks the server for the list of the tab it opens): sweeping the bar must not fire one per tab.
 *     `panels` says which panels exist: "all" (Write / Preview keep both mounted) or "current" (the
 *     default: the page renders the body of the current tab only), and only a tab whose panel is in the
 *     page carries `aria-controls` — a reference to nothing is an invalid attribute.
 *
 * One bar, drawn once: an underline, `brand` on the current tab and `ink-muted` on the others
 * (measured: 8.2:1 and 6.1:1 on the ivory ground, 7.9:1 and 5.8:1 on the `sunken` band of the
 * Markdown editor). `className` is LAYOUT only (a margin).
 */
export interface SectionTab {
  to: string;
  label: string;
}

/** A section of the console that has several pages: its name (the eyebrow) and its route tabs. */
export interface Section {
  label: string;
  tabs: readonly SectionTab[];
}

export interface StateTab<T extends string> {
  value: T;
  label: string;
}

const BAR = "flex gap-1 border-b border-line";
const TAB = "-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors";
const CURRENT = "border-brand-500 text-brand-700";
const OTHER = "border-transparent text-ink-muted hover:text-ink";

interface RouteProps {
  /** What the bar is, for a screen reader that lists landmarks ("Messaging pages"). */
  label: string;
  tabs: readonly SectionTab[];
  className?: string;
}

interface StateProps<T extends string> {
  /** What the tabs switch ("Editor mode"). */
  label: string;
  tabs: readonly StateTab<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Ties each tab to its `TabPanel`: unique on the page. */
  idPrefix: string;
  /** What the arrow keys do: select the tab they land on (default), or only move the focus. */
  activation?: "automatic" | "manual";
  /** Which panels are in the page: every tab's, or the current tab's only (default). */
  panels?: "all" | "current";
  className?: string;
}

const tabId = (idPrefix: string, value: string) => `${idPrefix}-tab-${value}`;
const panelId = (idPrefix: string, value: string) => `${idPrefix}-panel-${value}`;

function RouteTabs({ label, tabs, className = "" }: RouteProps) {
  return (
    <nav aria-label={label} className={`${BAR}${className ? ` ${className}` : ""}`}>
      {tabs.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          className={({ isActive }) => `${TAB} ${isActive ? CURRENT : OTHER}`}
        >
          {tab.label}
        </NavLink>
      ))}
    </nav>
  );
}

function StateTabs<T extends string>({
  label,
  tabs,
  value,
  onChange,
  idPrefix,
  activation = "automatic",
  panels = "current",
  className = "",
}: StateProps<T>) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const last = tabs.length - 1;
    let next: number;
    if (event.key === "ArrowRight") next = index === last ? 0 : index + 1;
    else if (event.key === "ArrowLeft") next = index === 0 ? last : index - 1;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = last;
    else return;
    event.preventDefault();
    if (activation === "automatic") onChange(tabs[next].value);
    refs.current[next]?.focus();
  }

  return (
    <div role="tablist" aria-label={label} className={`${BAR}${className ? ` ${className}` : ""}`}>
      {tabs.map((tab, index) => {
        const selected = tab.value === value;
        return (
          <button
            key={tab.value}
            ref={(el) => {
              refs.current[index] = el;
            }}
            type="button"
            role="tab"
            id={tabId(idPrefix, tab.value)}
            aria-selected={selected}
            aria-controls={selected || panels === "all" ? panelId(idPrefix, tab.value) : undefined}
            // Only the current tab is in the tab order: the arrows move within the bar.
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(tab.value)}
            onKeyDown={(event) => onKeyDown(event, index)}
            className={`${TAB} ${selected ? CURRENT : OTHER}`}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

export function SectionTabs<T extends string>(props: RouteProps | StateProps<T>) {
  return "value" in props ? <StateTabs {...props} /> : <RouteTabs {...props} />;
}

/**
 * What one state tab shows. `active={false}` hides it WITHOUT unmounting it (the Markdown editor
 * keeps Monaco, its undo stack and its cursor while the preview is up). A page with a single body
 * whose content follows the tab (the metadata cache) renders one panel with the current `value`.
 */
export function TabPanel({
  idPrefix,
  value,
  active = true,
  children,
}: {
  idPrefix: string;
  value: string;
  active?: boolean;
  children: ReactNode;
}) {
  return (
    <div role="tabpanel" id={panelId(idPrefix, value)} aria-labelledby={tabId(idPrefix, value)} hidden={!active}>
      {children}
    </div>
  );
}
